import React from "react";
import { signOut } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowRightOnRectangleIcon,
  CheckIcon,
  ComputerDesktopIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  GlobeAltIcon,
  NoSymbolIcon,
  MoonIcon,
  SunIcon,
  UserCircleIcon,
} from "@heroicons/react/20/solid";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "../../../../ui";
import { useUserInfo } from "../../../../features/profile/hooks/useUserInfo";
import { useAuthContext } from "../../../../features/auth/hooks";
import { useImpersonation } from "../../../../features/impersonation";
import { useTheme, type ThemePreference } from "../../../../features/theme";
import { REGIONS, getCurrentRegion } from "../../../../lib/regions";
import { APP_VERSION, formatBuildTime, versionSummary } from "../../../../lib/version";

const THEME_OPTIONS: {
  id: ThemePreference;
  label: string;
  icon: typeof SunIcon;
}[] = [
  { id: "light", label: "Light", icon: SunIcon },
  { id: "dark", label: "Dark", icon: MoonIcon },
  { id: "system", label: "System", icon: ComputerDesktopIcon },
];

/**
 * Top user/avatar menu (016 §4): personal Profile + Sign out, moved out of the nav
 * and the standalone /control-center page into a DropdownMenu in the sidebar footer.
 *
 * Composo staff (@composo.ai) additionally get a "View as customer…" entry that
 * opens the read-only impersonation picker.
 */
export function SidebarUserMenu() {
  const { userInfo } = useUserInfo();
  const { isStaff } = useAuthContext();
  const { stopImpersonation } = useImpersonation();
  const { preference, resolvedTheme, setTheme } = useTheme();
  const navigate = useNavigate();

  if (!userInfo) return null;

  const initial = userInfo.email.charAt(0).toUpperCase();
  const currentRegion = getCurrentRegion();

  const buildTime = formatBuildTime();

  // Copying beats reading it out: these tags are long and easy to transcribe
  // wrong into an issue.
  const handleCopyVersion = async () => {
    try {
      await navigator.clipboard.writeText(versionSummary());
      toast.success("Version copied");
    } catch (error) {
      console.error("Error copying version:", error);
    }
  };

  const handleSignOut = async () => {
    try {
      // Drop any "view as customer" scope so it can't bleed into the next
      // session. This is required, not just defensive: the target is persisted
      // to localStorage now, so it would otherwise survive the reload below (and
      // be visible in other tabs) instead of being cleared on sign-out.
      stopImpersonation();
      await signOut();
      navigate("/");
      window.location.reload();
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error("Error signing out");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="link-reset mx-2 flex w-[calc(100%-1rem)] items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
          {initial}
        </span>
        <span className="min-w-0 flex-1 truncate text-left text-muted-foreground">
          {userInfo.email}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-0.5">
            <span className="truncate text-sm font-medium">{userInfo.email}</span>
            <span className="truncate text-xs text-muted-foreground">
              {userInfo.organization}
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isStaff && (
          <DropdownMenuItem onSelect={() => navigate("/impersonate")}>
            <EyeIcon className="h-4 w-4" aria-hidden="true" />
            View as customer…
          </DropdownMenuItem>
        )}
        {isStaff && (
          <DropdownMenuItem onSelect={() => navigate("/suppressions")}>
            <NoSymbolIcon className="h-4 w-4" aria-hidden="true" />
            Suppressed emails…
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => navigate("/profile")}>
          <UserCircleIcon className="h-4 w-4" aria-hidden="true" />
          Profile
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <GlobeAltIcon className="h-4 w-4" aria-hidden="true" />
            Region
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {REGIONS.map((region) => {
              const isCurrent = region.id === currentRegion?.id;
              return (
                <DropdownMenuItem
                  key={region.id}
                  onSelect={() => {
                    // Different deployment + session: open in a new tab so the
                    // current region stays put. Not a client-side route.
                    if (!isCurrent)
                      window.open(region.url, "_blank", "noopener,noreferrer");
                  }}
                >
                  {region.label}
                  {isCurrent && (
                    <CheckIcon
                      className="ml-auto h-4 w-4"
                      aria-hidden="true"
                    />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            {resolvedTheme === "dark" ? (
              <MoonIcon className="h-4 w-4" aria-hidden="true" />
            ) : (
              <SunIcon className="h-4 w-4" aria-hidden="true" />
            )}
            Theme
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {THEME_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isCurrent = option.id === preference;
              return (
                <DropdownMenuItem
                  key={option.id}
                  onSelect={() => setTheme(option.id)}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {option.label}
                  {isCurrent && (
                    <CheckIcon className="ml-auto h-4 w-4" aria-hidden="true" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem onSelect={handleSignOut}>
          <ArrowRightOnRectangleIcon className="h-4 w-4" aria-hidden="true" />
          Sign out
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {/*
          Which build is this? Kept as the last row so it reads as a footnote
          rather than an action. `preventDefault` keeps the menu open, so a
          double-tap on the copy doesn't require reopening it.
        */}
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault();
            void handleCopyVersion();
          }}
          className="text-xs text-muted-foreground focus:text-muted-foreground"
          title={buildTime ? `Built ${buildTime} — click to copy` : "Click to copy"}
        >
          <DocumentDuplicateIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate font-mono">{APP_VERSION}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
